import { MODULES_BY_MASTER, moduleMeta } from "./oneStopModules";

/**
 * Rows -> the Branch -> Site -> Module -> Transaction type tree the report renders,
 * with figures rolled up at every level.
 *
 * The Site level only exists for a CUSTOMER report (it comes from
 * App_CustomerLocation.SiteLabel, which only customer-linked rows carry). For
 * Employee/Product/Vehicle the level is skipped entirely rather than rendered as
 * a single empty "Main" node - see hasSites on each branch.
 *
 * PRODUCT reports measure quantity, not money: a product 360 is a stock
 * question. Every other master measures money. That choice drives which column
 * the tree shows and which figure rolls up - see `unit`.
 */

const isMoney = (masterType) => masterType !== "PRODUCT";

// Direction NONE marks a document that only requests, approves or orders money
// another row actually moves - an LPO paid by a petty cash voucher, the
// maintenance request that voucher was raised from, a quotation that became an
// order (Batch165). Those rows still show their own amount, but are NEVER added
// to a total: until 2026-09-12 `value` summed every row, so one vehicle job
// counted its cash three times (request + voucher + LPO) - user: "if an lpo is
// created and petty cash is issued for that lpo it is not two transactions".
// Same reason Batch99 gave for LPOs on the stock report: a purchase order is
// not stock (or money) that has moved.
const signedValue = (row, money) => {
  const raw = money ? row.amount : row.quantity;
  if (raw == null) return 0;
  return Number(raw);
};

const movesMoney = (row) => row.direction === "IN" || row.direction === "OUT";

// referenceValue = the NONE rows' own sum, shown only on their own type line.
const emptyTotals = () => ({ count: 0, value: 0, inValue: 0, outValue: 0, referenceValue: 0 });

const addToTotals = (totals, row, money) => {
  const v = signedValue(row, money);
  totals.count += 1;
  if (!movesMoney(row)) {
    totals.referenceValue += v;
    return;
  }
  totals.value += v;
  if (row.direction === "IN") totals.inValue += Math.abs(v);
  else totals.outValue += Math.abs(v);
};

const upsert = (map, key, make) => {
  if (!map.has(key)) map.set(key, make());
  return map.get(key);
};

export const buildOneStopTree = (rows = [], masterType = "CUSTOMER") => {
  const money = isMoney(masterType);
  const branches = new Map();

  rows.forEach((row) => {
    const branch = upsert(branches, row.branchId, () => ({
      key: `b${row.branchId}`,
      branchId: row.branchId,
      branchCode: row.branchCode,
      branchName: row.branchName,
      totals: emptyTotals(),
      sites: new Map(),
      moduleCodesSeen: new Set(),
    }));
    addToTotals(branch.totals, row, money);
    branch.moduleCodesSeen.add(row.moduleCode);

    // One bucket per site; rows with no location fall into a single unlabelled
    // bucket that renders without a site header.
    const siteKey = row.locationId == null ? "_none" : String(row.locationId);
    const site = upsert(branch.sites, siteKey, () => ({
      key: `${branch.key}-s${siteKey}`,
      locationId: row.locationId ?? null,
      siteLabel: row.siteLabel ?? null,
      totals: emptyTotals(),
      modules: new Map(),
    }));
    addToTotals(site.totals, row, money);

    const mod = upsert(site.modules, row.moduleCode, () => ({
      key: `${site.key}-m${row.moduleCode}`,
      moduleCode: row.moduleCode,
      moduleName: row.moduleName || moduleMeta(row.moduleCode).label,
      totals: emptyTotals(),
      types: new Map(),
    }));
    addToTotals(mod.totals, row, money);

    // Keyed by direction too: one transaction type can hold both kinds (a CREDIT
    // LPO off a vehicle request is the bill, a CASH one is paid by the voucher),
    // and each group's line must say whether it is counted.
    const typeKey = `${row.transactionType}|${row.direction}`;
    const type = upsert(mod.types, typeKey, () => ({
      key: `${mod.key}-t${typeKey}`,
      transactionType: row.transactionType,
      direction: row.direction,
      totals: emptyTotals(),
      rows: [],
    }));
    addToTotals(type.totals, row, money);
    type.rows.push(row);
  });

  const expected = MODULES_BY_MASTER[masterType] || [];

  const tree = [...branches.values()]
    .map((branch) => {
      const sites = [...branch.sites.values()].map((site) => ({
        ...site,
        modules: [...site.modules.values()]
          .map((mod) => ({
            ...mod,
            types: [...mod.types.values()].sort((a, b) =>
              a.transactionType.localeCompare(b.transactionType)
            ),
          }))
          .sort((a, b) => a.moduleName.localeCompare(b.moduleName)),
      }));

      // A site header only makes sense when at least one row actually carried a
      // location - otherwise the level is noise.
      const hasSites = sites.some((s) => s.siteLabel);

      return {
        ...branch,
        sites: sites.sort((a, b) => (a.siteLabel || "").localeCompare(b.siteLabel || "")),
        hasSites,
        // Modules we would have reported for this master type but found nothing
        // for. Rendered as muted chips so a reader can tell "checked, empty"
        // apart from "never looked".
        quietModules: expected.filter((code) => !branch.moduleCodesSeen.has(code)),
      };
    })
    .sort((a, b) => a.branchName.localeCompare(b.branchName));

  return { tree, money };
};

/**
 * The KPI strip. Derived from the same rows the tree uses, so the headline
 * figures can never disagree with the detail underneath them.
 */
export const buildOneStopKpis = (rows = [], masterType = "CUSTOMER") => {
  const money = isMoney(masterType);

  const totals = emptyTotals();
  const modules = new Set();
  const branches = new Set();
  let firstDate = null;
  let lastDate = null;

  rows.forEach((row) => {
    addToTotals(totals, row, money);
    modules.add(row.moduleCode);
    branches.add(row.branchId);
    if (row.transactionDate) {
      const d = String(row.transactionDate).slice(0, 10);
      if (!firstDate || d < firstDate) firstDate = d;
      if (!lastDate || d > lastDate) lastDate = d;
    }
  });

  return {
    money,
    count: totals.count,
    inValue: totals.inValue,
    outValue: totals.outValue,
    netValue: totals.value,
    moduleCount: modules.size,
    branchCount: branches.size,
    firstDate,
    lastDate,
    voidedCount: rows.filter((r) => r.isVoided).length,
  };
};
